import React from 'react';
import { Toolbar, Box, Typography, TextField, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface FilterBarProps {
  title?: React.ReactNode;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  /** Whether any filter is active (controls Clear button visibility). */
  hasActiveFilters?: boolean;
  onClear?: () => void;
  /** Extra filter controls (selects, etc.). */
  children?: React.ReactNode;
  /** Right-aligned action buttons. */
  actions?: React.ReactNode;
}

/**
 * Standard list-page toolbar: title + search + arbitrary filter controls +
 * Clear + right-aligned actions. Keeps filter UIs consistent across pages.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  title,
  search,
  hasActiveFilters,
  onClear,
  children,
  actions,
}) => {
  return (
    <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, px: { xs: 1, sm: 2 } }}>
      {title && (
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {search && (
          <TextField
            placeholder={search.placeholder ?? 'Search…'}
            variant="outlined"
            size="small"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
        )}

        {children}

        {hasActiveFilters && onClear && (
          <Button variant="text" size="small" onClick={onClear}>
            Clear
          </Button>
        )}

        {actions}
      </Box>
    </Toolbar>
  );
};

export default FilterBar;
